import type {
  Feature,
  FeatureCollection,
  LineString,
  Point,
} from 'geojson/index.d.ts';
import polyline from '@mapbox/polyline';
import turfDistance from '@turf/distance';
import turfSimplify from '@turf/simplify';
import _compact from 'lodash/compact';
import {
  GeoJSONSourceSpecification,
  Layer,
  LngLatBounds,
  LngLatLike,
  Map as MapboxMap,
  Marker,
  Popup,
  PopupOptions,
} from 'mapbox-gl';
import {makeStaticMapUrl} from '../api/static-mapbox';
import {
  FeaturePoint,
  TerminalPoint,
  TripData,
  TripMarkerConfig,
  TripPointEdge,
  TripStop,
} from '../api/trips-api';
import {tripsGeoJson, tripStopsGeoJson} from '../map/sources';
import {stopsCircle, stopsLabel, tripDirectionArrows, tripLine} from './layers';
import {
  addImages,
  CONTINENTAL_US_BOUNDS,
  DEFAULT_PADDING,
  extractZoomLevelsAndRadii,
  interpolateRadius,
} from './map';
import MapImage from './MapImage';
import {
  createStaticStopMarker,
  createStaticTerminalMarker,
  createTerminalMarker,
} from './trip-marker';
import tripStopPopup from './trip-stop-popup';

export const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

export const tripMarkerConfig = Object.freeze({
  0: {
    anchor: 'top-left',
    offset: [9, 1],
    style: {
      top: '-6px',
      left: '-7px',
      transform: 'rotate(120deg)',
      filter: 'drop-shadow(1px 2px 1px rgba(0,0,0,0.3))',
    },
  },
  4: {
    anchor: 'top',
    offset: [-5, 13],
    style: {
      top: '-15px',
      left: '50%',
      transform: 'rotate(180deg)',
      filter: 'drop-shadow(0px 1px 1px rgba(0,0,0,0.3))',
    },
  },
  1: {
    anchor: 'top-right',
    offset: [-9, 1],
    style: {
      top: '-6px',
      right: '-7px',
      transform: 'rotate(240deg)',
      filter: 'drop-shadow(-1px 2px 1px rgba(0,0,0,0.3))',
    },
  },
  2: {
    anchor: 'bottom-right',
    offset: [-7, -4],
    style: {
      bottom: '-10px',
      right: '-5px',
      transform: 'rotate(300deg)',
      filter: 'drop-shadow(-1px 2px 1px rgba(0,0,0,0.3))',
    },
  },
  5: {
    anchor: 'bottom',
    offset: [-5, -16],
    style: {
      bottom: '-19px',
      left: '50%',
      transform: 'rotate(0deg)',
      filter: 'drop-shadow(0px 2px 1px rgba(0,0,0,0.3))',
    },
  },
  3: {
    anchor: 'bottom-left',
    offset: [6, -4],
    style: {
      bottom: '-10px',
      left: '-5px',
      transform: 'rotate(60deg)',
      filter: 'drop-shadow(1px 2px 1px rgba(0,0,0,0.3))',
    },
  },
}) as Readonly<Record<number, TripMarkerConfig>>;

let terminalPointMarkers = [] as Marker[];

export async function renderStatic({
  container,
  style,
  token,
  trips,
}: {
  container: HTMLElement;
  style?: string;
  token: string;
  trips: TripData[];
}) {
  if (trips.length === 0) {
    return '';
  }

  const height = container.clientHeight;
  const width = container.clientWidth;

  // Add trip stop labels
  const tripsWithLabels = await addTripStopLabels(trips);
  // Add trip stops source
  const tripStops = await getTripStops(tripsWithLabels);
  const tripStopMarkers = await Promise.all(
    tripStops.map((point) => createStaticStopMarker(point))
  );

  // Get trip start/end
  const terminalPoints = await getTerminalPoints(trips);
  const terminalPointMarkers = await Promise.all(
    terminalPoints.map((point) => createStaticTerminalMarker(point))
  );

  function staticMapUrl(tripPolylines: string) {
    return makeStaticMapUrl({
      height,
      width,
      polyline: tripPolylines,
      markers: [...tripStopMarkers, ...terminalPointMarkers],
      style,
      token,
    });
  }

  function combinePolylines(encodedPolylines: string[], simplified = false) {
    const polylines = [] as string[];

    for (const encodedPolyline of encodedPolylines) {
      const polyineParam = `${`path-4+5742e0-1(${encodeURIComponent(
        encodedPolyline
      )})`}`;

      const nextPolylineString = [...polylines, polyineParam].join(',');
      const nextRequestUrl = staticMapUrl(nextPolylineString);

      if (nextRequestUrl.length > 8000) {
        // we exceeded the max, ~8192

        if (simplified) {
          // We've already simplified as much as we could
          throw new Error(
            'Unable to create Static Map from trips: Too many trip points. Please select a date range that includes fewer trips.'
          );
        }

        const simpflifiedPolylines = getSimplifiedTripPolylines(trips);

        return combinePolylines(simpflifiedPolylines, true);
      }

      polylines.push(nextPolylineString);
    }

    return polylines.join(',');
  }

  const combinedPolylines = combinePolylines(
    trips.map(({encoded_polyline}) => encoded_polyline)
  );

  // If it fits into one call, just return the URL
  return staticMapUrl(combinedPolylines);
}

export async function renderInteractive({
  trips,
  map,
}: {
  trips: TripData[];
  map?: MapboxMap;
  reverseGeocode?: boolean;
}) {
  if (!map) {
    return false;
  }

  const resetSuccess = resetMappedTrips(map);
  if (!resetSuccess) {
    return false;
  }

  if (trips.length > 0) {
    // Add trip stop labels
    const tripsWithLabels = await addTripStopLabels(trips);

    // Make sure map style has not changed since previous async action
    if (!isMapStyleLoaded(map)) {
      return false;
    }

    // Add trip line source
    const tripsSource = tripsGeoJson(tripsWithLabels);
    map.addSource('trips', <GeoJSONSourceSpecification>tripsSource);

    // Add trip stops source
    const tripStops = await getTripStops(tripsWithLabels);
    const tripStopsSource = tripStopsGeoJson(tripStops);
    map.addSource('trip-stops', tripStopsSource);

    // Add trip start/end markers
    const terminalPoints = getTerminalPoints(trips);
    terminalPointMarkers = terminalPoints.map((point) =>
      new Marker({
        anchor: point.anchor,
        offset: point.offset,
        element: createTerminalMarker(point),
      })
        .setLngLat(point.coordinates)
        .addTo(map)
    );

    // Add images
    await addImages(map, [
      new MapImage(
        'trip-direction-arrow',
        'img:https://res.cloudinary.com/zubie/image/upload/v1752164581/apps/web/trip-direction-arrow.png'
      ),
    ]);

    // Make sure map style has not changed or sources have gone missing since previous async action
    if (
      !isMapStyleLoaded(map) ||
      !map.getSource('trips') ||
      !map.getSource('trip-stops')
    ) {
      return false;
    }

    // Render trip lines
    map.addLayer({
      ...tripLine,
      source: 'trips',
    } as Layer);

    // Render trip direction arrows
    map.addLayer({
      ...tripDirectionArrows,
      source: 'trips',
    } as Layer);

    // Render trip stops
    map.addLayer({
      ...stopsCircle,
      source: 'trip-stops',
    } as Layer);
    map.addLayer({
      ...stopsLabel,
      source: 'trip-stops',
    } as Layer);

    const tripStopsCircleRadiiConfig = extractZoomLevelsAndRadii(
      map,
      'trip-stops-circle'
    );

    map.addInteraction('trip-stops-click', {
      type: 'click',
      target: {layerId: 'trip-stops-label'},
      handler: (event) => {
        const geometry = event?.feature?.geometry as Point;
        const coordinates = geometry?.coordinates as [number, number];

        if (!coordinates) {
          return;
        }

        let offset;
        if (tripStopsCircleRadiiConfig) {
          const zoom = map.getZoom();

          // Match your interpolate values here
          const radius = interpolateRadius(zoom, tripStopsCircleRadiiConfig);
          offset = [0, -radius] as PopupOptions['offset'];
        }

        const featurePoint = event?.feature?.properties as FeaturePoint;
        const point = convertFeaturePoint(featurePoint);

        new Popup({
          offset: offset,
        })
          .setLngLat(coordinates)
          .setHTML(tripStopPopup(point).innerHTML)
          .addTo(map);
      },
    });

    map.addInteraction('trip-stops-mouseenter', {
      type: 'mouseenter',
      target: {layerId: 'trip-stops-label'},
      handler: () => {
        map.getCanvas().style.cursor = 'pointer';
      },
    });

    map.addInteraction('trip-stops-mouseleave', {
      type: 'mouseleave',
      target: {layerId: 'trip-stops-label'},
      handler: () => {
        map.getCanvas().style.cursor = '';
      },
    });

    // Fit map to trip bounds
    const bounds = await getTripBounds(
      tripsSource.data as FeatureCollection<LineString>
    );

    // Zoom to fit all trip lines
    map.fitBounds(bounds, {
      padding: DEFAULT_PADDING,
      duration: 2000,
    });
    // this.hasUserChangedBounds = false;
    map.resize();
  }

  return true;
}

export function getSimplifiedTripPolylines(trips: TripData[]) {
  return trips.map(({encoded_polyline}) => {
    const coords = polyline.decode(encoded_polyline);

    const geojsonLine = {
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: coords.map(([lat, lng]) => [lng, lat]),
      },
    } as Feature;

    const simplifiedPolyline = turfSimplify(geojsonLine, {
      tolerance: 0.001, // units in degrees (~111m at equator)
      highQuality: true, // Douglas-Peucker algorithm
    });

    const simplifiedCoords = (<LineString>(
      simplifiedPolyline.geometry
    )).coordinates.map(([lng, lat]) => [lat, lng]);

    return polyline.encode(simplifiedCoords as [number, number][], 5); // use 4 for lower precision
  });
}

export async function getTripBounds(
  featureCollection: FeatureCollection<LineString>
) {
  // Use FeatureCollection type to access features;
  if (featureCollection.features?.length) {
    const bounds = new LngLatBounds();
    featureCollection.features.forEach((feature) => {
      const coords = feature?.geometry?.coordinates || [];
      if (coords.length > 0) {
        coords.forEach((point) => bounds.extend(point as LngLatLike));
      }
    });
    return bounds;
  }

  return new LngLatBounds(
    CONTINENTAL_US_BOUNDS.southWest,
    CONTINENTAL_US_BOUNDS.northEast
  );
}

export async function getTripStops(trips: Awaited<TripData[]>) {
  const stops = trips.map(
    async (
      {
        key: tripKey,
        index: tripIndex,
        label,
        /** media, **/ start_point: departure,
      },
      index
    ) => {
      const nextTrip = index < trips.length - 1 ? trips[index + 1] : null;
      if (nextTrip) {
        const arrival = nextTrip.end_point;
        if (
          isValidPoint(departure) &&
          isValidPoint(arrival) &&
          (await shouldCombinePoints(departure, arrival))
        ) {
          // const departureMedia = getTripStartMedia(media);
          // const arrivalMedia = getTripEndMedia(arrival.media);
          return {
            // arrival: {...arrival, media: arrivalMedia},
            arrival: {...arrival},
            coordinates: [departure.longitude, departure.latitude],
            // departure: {...departure, media: departureMedia},
            departure: {...departure},
            id: `${nextTrip.key}:${tripKey}`,
            label,
            tripIndex,
          } as TripStop;
        }
      }
      return null;
    }
  );

  return _compact(await Promise.all(stops));
}

export async function addTripStopLabels(trips: TripData[]) {
  const stopLabels = [
    ...trips.map((_, index) => {
      if (index >= 0) {
        const alphabetLoops = Math.floor(index / ALPHABET.length) - 1;
        const mod = index % ALPHABET.length;
        const letters = ALPHABET.split('');
        const firstChar = alphabetLoops >= 0 ? letters[alphabetLoops] : '';
        const label = `${firstChar}${letters[mod]}`;
        return label;
      }
      return '';
    }),
  ].reverse();

  return trips.map((trip, index) => ({
    ...trip,
    index,
    label: stopLabels[index + 1],
  }));
}

export function isValidPoint(point: TripPointEdge) {
  return Boolean(
    point && point.latitude && point.longitude && point.timestamp_utc
  );
}

export function getTerminalPoints(trips: TripData[]) {
  const points = [] as Array<TerminalPoint>;

  const firstTrip = trips[trips.length - 1];
  const lastTrip = trips[0];

  const firstPoint = firstTrip.start_point;
  const lastPoint = lastTrip.end_point;

  [firstPoint, lastPoint].forEach((point, index) => {
    if (isValidPoint(point)) {
      const direction = index === 0 ? 4 : 5; // up or down
      const type = index === 0 ? 'departure' : 'arrival';
      // const trip = index === 0 ? firstTrip : lastTrip;
      // const {media} = trip;
      // const matchingMedia =
      //   index === 0 ? getTripStartMedia(media) : getTripEndMedia(media);
      const {anchor, offset} = tripMarkerConfig[direction];
      points.push({
        anchor,
        coordinates: [point.longitude, point.latitude],
        direction,
        id: `${point.timestamp_utc}-${type}`,
        offset,
        [type]: {...point},
      });
    }
  });

  return points;
}

export function resetMappedTrips(map?: MapboxMap) {
  if (!map || !isMapStyleLoaded(map)) {
    return false;
  }

  // Remove any popups
  [...map.getContainer().getElementsByClassName('mapboxgl-popup')].forEach(
    (popup) => {
      popup.remove();
    }
  );

  // Clear existing interactions
  map.removeInteraction('trip-stops-click');
  map.removeInteraction('trip-stops-mouseenter');
  map.removeInteraction('trip-stops-mouseleave');

  // Clear existing layers
  const existingLayers = map.getStyle().layers || [];
  existingLayers.forEach((layer) => {
    if (layer.id.startsWith('trip-')) {
      map.removeLayer(layer.id);
    }
  });

  // Clear existing sources
  ['trips', 'trip-stops'].forEach((sourceName) => {
    if (map.getSource(sourceName)) {
      map.removeSource(sourceName);
    }
  });

  // Remove existing markers
  terminalPointMarkers.forEach((marker) => {
    marker.remove();
  });

  return true;
}

async function shouldCombinePoints(
  point: TripPointEdge,
  nextPoint: TripPointEdge
) {
  const nearby = await arePointsNearby(point, nextPoint);
  return point && nextPoint && nearby;
}

async function arePointsNearby(
  point: TripPointEdge,
  anotherPoint: TripPointEdge
) {
  // are points within 75 meters of each other (~ the 250ft used in V2)
  const km = await turfDistance(
    [point.longitude, point.latitude],
    [anotherPoint.longitude, anotherPoint.latitude],
    {
      units: 'kilometers',
    }
  );
  return km <= 0.075;
}

function convertFeaturePoint(featurePoint: FeaturePoint): TripStop {
  function parseJson<T>(value: string): T | null {
    try {
      return JSON.parse(value) as T;
    } catch {
      // ignore
    }
    return null;
  }

  return {
    arrival:
      parseJson<TripPointEdge>(featurePoint.arrival) || ({} as TripPointEdge),
    coordinates: parseJson<[number, number]>(featurePoint.coordinates) || [
      0, 0,
    ],
    departure:
      parseJson<TripPointEdge>(featurePoint.departure) || ({} as TripPointEdge),
    id: featurePoint.id,
    label: featurePoint.label,
    tripIndex: featurePoint.tripIndex,
  };
}

function isMapStyleLoaded(map: MapboxMap) {
  try {
    map.getStyle();
    return true;
  } catch {
    // Map style was changed, wait for next render
    return false;
  }
}
